import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { categories, getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4 block"></i>
            <h2 className="text-xl font-bold text-foreground-900 mb-2">文章不存在</h2>
            <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = getRelatedArticles(article, 3);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料不打烊',
      url: 'https://hqsls.cn',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://hqsls.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <article>
            {/* Article Header */}
            <header className="relative w-full h-[350px] md:h-[450px] overflow-hidden">
              <img
                src={article.coverImage}
                alt={article.title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/20"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
                <div className="max-w-4xl mx-auto">
                  <div className="flex items-center gap-2 mb-3">
                    {category && (
                      <Link
                        to={`/category/${category.slug}`}
                        className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-white cursor-pointer whitespace-nowrap"
                      >
                        {category.name}
                      </Link>
                    )}
                    {article.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white whitespace-nowrap">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-white/70">
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i>{article.author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>{formatDate(article.publishedAt)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>{(article.views / 10000).toFixed(1)}万阅读
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-chat-3-line"></i>{article.comments}评论
                    </span>
                  </div>
                </div>
              </div>
            </header>

            <div className="w-full px-4 md:px-6 py-6">
              <div className="max-w-4xl mx-auto">
                <Breadcrumb
                  items={[
                    { label: '首页', href: '/' },
                    { label: category?.name || '', href: `/category/${article.category}` },
                    { label: article.title },
                  ]}
                />
              </div>
            </div>

            {/* Article Content */}
            <div className="w-full px-4 md:px-6 pb-10">
              <div className="max-w-4xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Main Content */}
                  <div className="flex-1 min-w-0">
                    <div
                      className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
                        prose-h3:text-xl prose-h3:font-bold prose-h3:text-foreground-900 prose-h3:mt-8 prose-h3:mb-4
                        prose-p:mb-5 prose-p:leading-loose
                        [&_h3]:border-l-4 [&_h3]:border-primary-500 [&_h3]:pl-4"
                      dangerouslySetInnerHTML={{ __html: article.content }}
                    />

                    {/* Tags */}
                    <div className="mt-10 pt-6 border-t border-background-200/70">
                      <div className="flex flex-wrap gap-2">
                        {article.tags.map((tag) => (
                          <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium bg-background-100 text-foreground-600 whitespace-nowrap">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Share */}
                    <div className="mt-6 flex items-center gap-3">
                      <span className="text-sm text-foreground-500">分享到：</span>
                      <a href="#" className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 hover:bg-primary-500 hover:text-white text-foreground-600 transition-colors cursor-pointer" rel="nofollow" aria-label="分享到微信">
                        <i className="ri-wechat-line text-sm"></i>
                      </a>
                      <a href="#" className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 hover:bg-primary-500 hover:text-white text-foreground-600 transition-colors cursor-pointer" rel="nofollow" aria-label="分享到微博">
                        <i className="ri-weibo-line text-sm"></i>
                      </a>
                      <a href="#" className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 hover:bg-primary-500 hover:text-white text-foreground-600 transition-colors cursor-pointer" rel="nofollow" aria-label="复制链接">
                        <i className="ri-link text-sm"></i>
                      </a>
                    </div>
                  </div>

                  {/* Sidebar */}
                  <aside className="w-full lg:w-[280px] flex-shrink-0">
                    <div className="sticky top-20 space-y-6">
                      {/* Author Card */}
                      <div className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <i className="ri-user-3-line text-primary-500"></i>
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                            <p className="text-xs text-foreground-500">资深爆料编辑</p>
                          </div>
                        </div>
                        <p className="text-xs text-foreground-600 leading-relaxed">
                          专注娱乐八卦报道，致力于为读者带来最新、最真实的娱乐圈爆料。
                        </p>
                      </div>

                      {/* Related Articles */}
                      {relatedArticles.length > 0 && (
                        <div className="bg-background-50 rounded-lg border border-background-200/70 p-5">
                          <h3 className="text-base font-bold text-foreground-900 mb-4">相关爆料</h3>
                          <div className="space-y-1">
                            {relatedArticles.map((rel) => (
                              <ArticleCard
                                key={rel.id}
                                {...rel}
                                categoryName=""
                                variant="compact"
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </aside>
                </div>
              </div>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
}